/**
 * O texto do reprocessamento por IA — o antes/depois da amostra, o
 * resumo da estimativa e os motivos de pulo. Puro e testado, como o
 * `bulk-patch.ts` do lote: o diálogo só desenha.
 */
import {
  AI_REPROCESS_TASKS,
  type AiReprocessEstimate,
  type AiReprocessItem,
  type AiReprocessItemOutcome,
  type AiReprocessRun
} from "@/shared/api/api-client";
import { polarityLabel } from "@/shared/types/polarity";
import { costLabel } from "@/features/admin/ai-usage-view";
import { APPROACH_CHOICES } from "@/features/mentions/mention-filter-criteria";
import { fieldLabels } from "@/features/mentions/MentionClassificationEditor";

/** A execução ainda vai andar? `QUEUED` e `STARTED`. */
export function isAiReprocessRunning(run: AiReprocessRun): boolean {
  return run.status === "QUEUED" || run.status === "STARTED";
}

/** Vale continuar acompanhando? As que andam e a `ERRORED` que a fila
 *  ainda vai retomar (`retrying`, calculado no servidor) — sem isso a
 *  tela dizia "falhou" e parava enquanto a execução voltava a gravar. */
export function isAiReprocessTracked(run: AiReprocessRun): boolean {
  return isAiReprocessRunning(run) || run.retrying === true;
}

function approachLabel(value: unknown): string {
  if (value === null || value === undefined) return "Sem classificação";
  return (
    APPROACH_CHOICES.find(choice => choice.value === value)?.label ?? String(value)
  );
}

function asList(value: unknown): number[] {
  return Array.isArray(value) ? value.filter((item): item is number => typeof item === "number") : [];
}

/**
 * Uma linha legível de UM campo do diff.
 *
 * Polaridade e abordagem dizem o valor (é o que a pessoa quer conferir
 * na amostra). As listas dizem quantos entram e saem: o diff traz ids,
 * e resolver o nome de cada tema aqui exigiria o catálogo de todas as
 * marcas do recorte — a contagem já responde "a IA mexeu nisso?".
 */
export function describeChange(
  field: string,
  change: { old: unknown; new: unknown }
): string {
  const label = fieldLabels[field] ?? field;
  if (field === "polarity") {
    return `${label}: ${polarityLabel(change.old as string | null)} → ${polarityLabel(change.new as string | null)}`;
  }
  if (field === "approach") {
    return `${label}: ${approachLabel(change.old)} → ${approachLabel(change.new)}`;
  }
  if (field === "theme_percentages") return `${label} recalculado`;
  if (field === "highlight_theme_id") {
    return change.new === null ? `${label} desfeito` : `${label} alterado`;
  }
  const before = asList(change.old);
  const after = asList(change.new);
  const added = after.filter(item => !before.includes(item)).length;
  const removed = before.filter(item => !after.includes(item)).length;
  const parts = [
    added ? `+${added}` : null,
    removed ? `−${removed}` : null
  ].filter(Boolean);
  return `${label}: ${parts.length ? parts.join(" / ") : "reordenado"}`;
}

/**
 * O mesmo campo de `describeChange`, em pedaços pra tela desenhar o
 * antes → depois lado a lado. Valor (polaridade, abordagem) vem em
 * `before`/`after`; lista vem em `added`/`removed`; o resto é `note`.
 */
export type ChangeParts = {
  field: string;
  label: string;
  before?: string;
  after?: string;
  added?: number;
  removed?: number;
  note?: string;
};

export function changeParts(
  field: string,
  change: { old: unknown; new: unknown }
): ChangeParts {
  const label = fieldLabels[field] ?? field;
  if (field === "polarity") {
    return {
      field,
      label,
      before: polarityLabel(change.old as string | null),
      after: polarityLabel(change.new as string | null)
    };
  }
  if (field === "approach") {
    return { field, label, before: approachLabel(change.old), after: approachLabel(change.new) };
  }
  if (field === "theme_percentages") return { field, label, note: "recalculado" };
  if (field === "highlight_theme_id") {
    return { field, label, note: change.new === null ? "desfeito" : "trocado" };
  }
  const before = asList(change.old);
  const after = asList(change.new);
  const added = after.filter(item => !before.includes(item)).length;
  const removed = before.filter(item => !after.includes(item)).length;
  if (!added && !removed) return { field, label, note: "só a ordem mudou" };
  return { field, label, added, removed };
}

/** Os pedaços de todas as mudanças de uma menção, na ordem da tela. */
export function diffParts(
  diff: Record<string, { old: unknown; new: unknown }> | null
): ChangeParts[] {
  if (!diff) return [];
  const order = Object.keys(fieldLabels);
  return Object.keys(diff)
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map(field => changeParts(field, diff[field]));
}

/** O placar da amostra: quantas a IA mudaria, quantas ficariam iguais,
 *  quantas ficaram de fora e quantas deram erro. */
export type SampleSummary = {
  changed: AiReprocessItem[];
  unchanged: AiReprocessItem[];
  skipped: AiReprocessItem[];
  failed: AiReprocessItem[];
};

export function summarizeSample(items: AiReprocessItem[]): SampleSummary {
  const summary: SampleSummary = { changed: [], unchanged: [], skipped: [], failed: [] };
  for (const item of items) {
    if (item.outcome === "failed") summary.failed.push(item);
    else if (item.outcome?.startsWith("skipped_")) summary.skipped.push(item);
    else if (item.outcome === "unchanged") summary.unchanged.push(item);
    else summary.changed.push(item);
  }
  return summary;
}

/** Todas as linhas do diff de uma menção, na ordem em que a tela lê. */
export function describeDiff(
  diff: Record<string, { old: unknown; new: unknown }> | null
): string[] {
  if (!diff) return [];
  const order = Object.keys(fieldLabels);
  return Object.keys(diff)
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map(field => describeChange(field, diff[field]));
}

export const OUTCOME_LABELS: Record<AiReprocessItemOutcome, string> = {
  changed: "Alterada",
  unchanged: "Sem mudança",
  previewed: "Mudaria",
  skipped_reviewed: "Pulada: revisada",
  skipped_discarded: "Pulada: descartada",
  skipped_conflict: "Pulada: alterada depois do pedido",
  failed: "Erro"
};

/** "até US$ 0,42", ou o motivo de não haver número. */
export function estimateCostLabel(estimate: AiReprocessEstimate): string {
  if (estimate.eligible === 0) return "sem custo: nenhuma menção entra";
  if (estimate.estimated_cost === null) {
    return "sem base de custo — não há consumo com preço apurado nos últimos 30 dias";
  }
  return `até ${costLabel(String(estimate.estimated_cost), "USD", 2)}`;
}

/** Quem fica de fora e por quê — só os motivos que aconteceram. */
export function estimateSkippedLabel(estimate: AiReprocessEstimate): string | null {
  const parts = [
    estimate.skipped_reviewed &&
      `${estimate.skipped_reviewed.toLocaleString("pt-BR")} revisada(s)`,
    estimate.skipped_discarded &&
      `${estimate.skipped_discarded.toLocaleString("pt-BR")} descartada(s)`,
    estimate.skipped_unclassified &&
      `${estimate.skipped_unclassified.toLocaleString("pt-BR")} sem classificação`
  ].filter(Boolean);
  return parts.length ? `Ficam de fora: ${parts.join(", ")}.` : null;
}

/** Um pedaço da barra de andamento: um desfecho e quantas menções já
 *  caíram nele. A ordem é a da leitura — o que a IA mudou vem primeiro. */
export type ProgressSegment = {
  key: "changed" | "unchanged" | "skipped" | "failed";
  label: string;
  count: number;
};

export function progressSegments(run: AiReprocessRun): ProgressSegment[] {
  const { counters } = run;
  // Na amostra nada é gravado: o que mudaria conta em `previewed`.
  const changed = run.mode === "sample" ? counters.previewed : counters.changed;
  const segments: ProgressSegment[] = [
    {
      key: "changed",
      label: run.mode === "sample"
        ? changed === 1 ? "mudaria" : "mudariam"
        : changed === 1 ? "alterada" : "alteradas",
      count: changed
    },
    { key: "unchanged", label: "sem mudança", count: counters.unchanged },
    {
      key: "skipped",
      label: counters.skipped === 1 ? "pulada" : "puladas",
      count: counters.skipped
    },
    { key: "failed", label: "com erro", count: counters.failed }
  ];
  return segments.filter(segment => segment.count > 0);
}

/**
 * "faltam cerca de 3 min", pelo ritmo desde que a execução começou.
 * Nulo enquanto não há base honesta pra prever: ainda na fila, poucas
 * menções prontas ou poucos segundos de trabalho — um chute no começo
 * pula de "1 min" pra "40 min" e só atrapalha.
 */
export function progressEtaLabel(run: AiReprocessRun, nowMs: number): string | null {
  if (!run.started_at) return null;
  const { total, pending } = run.counters;
  const done = total - pending;
  const elapsed = nowMs - new Date(run.started_at).getTime();
  if (done < 2 || pending <= 0 || elapsed < 5000) return null;
  const remainingMinutes = (elapsed / done) * pending / 60000;
  if (remainingMinutes < 1) return "falta menos de 1 min";
  if (remainingMinutes < 60) return `faltam cerca de ${Math.round(remainingMinutes)} min`;
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = Math.round(remainingMinutes % 60);
  return `faltam cerca de ${hours} h${minutes ? ` ${minutes} min` : ""}`;
}

/** O selo de uma execução na lista de reprocessamentos, nos MESMOS tons
 *  do card de upload (`""` andando, `success`, `warning` — erro também é
 *  `warning` lá, e quem diferencia é o texto). `ERRORED` que a fila vai
 *  retomar não é "parou" — é "retomando", e continua sendo acompanhado. */
export function runStatusMeta(run: AiReprocessRun): {
  label: string;
  tone: "" | "success" | "warning";
} {
  if (run.status === "QUEUED") return { label: "Na fila", tone: "" };
  if (run.status === "STARTED") return { label: "Reprocessando", tone: "" };
  if (run.status === "ERRORED") {
    return run.retrying
      ? { label: "Retomando após erro", tone: "" }
      : { label: "Parou com erro", tone: "warning" };
  }
  return run.counters.failed > 0
    ? { label: "Concluído com erros", tone: "warning" }
    : { label: "Concluído", tone: "success" };
}

/** As tarefas pedidas, com o rótulo da tela ("Polaridade, Temas"). */
export function taskLabels(run: AiReprocessRun): string {
  return run.tasks
    .map(task => AI_REPROCESS_TASKS.find(item => item.value === task)?.label ?? task)
    .join(", ");
}

/** Chaves da foto do recorte que não são filtro de conteúdo: o período
 *  e o escopo são ditos à parte, e os `exclude_*` só invertem o filtro
 *  vizinho. */
const NOT_A_DIMENSION = /^(date_from|date_to|scope|q|exclude_.*)$/;

/**
 * O recorte de uma execução, em palavras: "312 menções marcadas à mão"
 * ou "todas do filtro" com o período e quantos filtros havia.
 *
 * A foto (`filter_labels`) é a querystring da API, com IDS — dá pra
 * dizer quantos filtros e qual período, não o nome de cada tema. Quem
 * entrou de verdade são os itens congelados da execução, não isto.
 */
export function describeTarget(run: AiReprocessRun): string {
  const labels = run.filter_labels;
  if (!labels) return "Recorte não registrado";
  if (typeof labels.selection === "number") {
    const n = labels.selection;
    return `${n.toLocaleString("pt-BR")} ${n === 1 ? "menção marcada" : "menções marcadas"} à mão`;
  }
  const first = (key: string): string | null => {
    const value = labels[key];
    const item = Array.isArray(value) ? value[0] : value;
    return typeof item === "string" && item ? item : null;
  };
  const day = (value: string | null) =>
    value ? value.slice(0, 10).split("-").reverse().join("/") : null;
  const from = day(first("date_from"));
  const to = day(first("date_to"));
  const parts = ["Todas do filtro"];
  if (from && to) parts.push(`de ${from} a ${to}`);
  else if (from) parts.push(`a partir de ${from}`);
  else if (to) parts.push(`até ${to}`);
  const search = first("q");
  if (search) parts.push(`busca “${search}”`);
  const dimensions = Object.keys(labels).filter(key => {
    const value = labels[key];
    return !NOT_A_DIMENSION.test(key) && Array.isArray(value) && value.length > 0;
  }).length;
  if (dimensions) parts.push(`${dimensions} ${dimensions === 1 ? "filtro" : "filtros"}`);
  const scope = first("scope");
  if (scope === "competitors") parts.push("só concorrentes");
  if (scope === "all") parts.push("empresa e concorrentes");
  return parts.join(" · ");
}

/** As abas do detalhe: cada uma pede ao servidor só os desfechos dela,
 *  e o número vem dos contadores da execução (não da lista, que tem
 *  teto). */
export type RunItemTab = "changed" | "unchanged" | "skipped" | "failed";

export const RUN_ITEM_TABS: {
  key: RunItemTab;
  label: string;
  outcomes: AiReprocessItemOutcome[];
}[] = [
  { key: "changed", label: "Alteradas", outcomes: ["changed"] },
  { key: "unchanged", label: "Sem mudança", outcomes: ["unchanged"] },
  {
    key: "skipped",
    label: "Puladas",
    outcomes: ["skipped_reviewed", "skipped_discarded", "skipped_conflict"]
  },
  { key: "failed", label: "Com erro", outcomes: ["failed"] }
];

export function tabCount(run: AiReprocessRun, tab: RunItemTab): number {
  return run.counters[tab];
}
